import React, { useEffect, useState } from 'react';
import api from '../../api';       
import config from '../../config';
import { FaTrash, FaPlus, FaMapMarkerAlt, FaCloudUploadAlt } from 'react-icons/fa';
// Import CSS Module
import styles from './Admin.module.css';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    category: 'Residential', 
    location: '',
    imageFile: null
  });
  const [message, setMessage] = useState({ text: '', type: '' });



  // 1. Fetch Images on Load
  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
    const res = await api.get('/Gallery');
      setImages(res.data);
    } catch (err) {
      console.error("Error fetching images:", err);
    }
  };

  // 2. Handle Input Change
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Size Validation (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: "File size too large! Max 5MB allowed.", type: "error" });
        e.target.value = ""; 
        return;
      }

      // Type Validation
      if (!file.type.startsWith('image/')) {
        setMessage({ text: "Only image files are allowed!", type: "error" });
        e.target.value = "";
        return;
      }

      setFormData({ ...formData, imageFile: file });
      setMessage({ text: "", type: "" }); 
    }
  };

  // 3. Handle Submit (Upload)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageFile) {
      setMessage({ text: "Please select an image!", type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const data = new FormData();
    data.append('Title', formData.title);
    data.append('Category', formData.category);
    data.append('Location', formData.location);
    data.append('ImageFile', formData.imageFile);

    try {
      const token = localStorage.getItem('token');
    await api.post('/Gallery', data, {
  headers: {
    'Content-Type': 'multipart/form-data'
  }
});

      setMessage({ text: "Image Uploaded Successfully!", type: "success" });
      fetchImages(); 
      setFormData({ title: '', category: 'Residential', location: '', imageFile: null });
      document.getElementById('fileInput').value = ""; 

    } catch (err) {
      console.error(err);
      setMessage({ text: "Upload Failed! Check server logs.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // 4. Handle Delete
  const handleDelete = async (id) => {
    if(!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const token = localStorage.getItem('token');
     await api.delete(`/Gallery/${id}`);
      setImages(images.filter(img => img.id !== id));
    } catch (err) {
      alert("Failed to delete image");
    }
  };

  return (
    <div>
      {/* --- Upload Form Section --- */}
      {/* Used styles.formCard from Admin.module.css */}
      <div className={styles.formCard}>
        <h3 style={{ marginBottom: '1.5rem', color: '#4ade80', display:'flex', alignItems:'center', gap:'10px' }}>
          <FaPlus /> Add New Project Image
        </h3>

        {/* Message Alert Box */}
        {message.text && (
          <div style={{
            padding: '12px', marginBottom: '20px', borderRadius: '8px',
            background: message.type === 'error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(74, 222, 128, 0.15)',
            color: message.type === 'error' ? '#fca5a5' : '#4ade80',
            border: message.type === 'error' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(74, 222, 128, 0.3)',
            fontSize: '0.9rem'
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
          
          {/* Left Column */}
          <div>
            <div className={styles.formGroup}>
              <label>Project Title</label>
              <input 
                type="text" 
                name="title" 
                className={styles.formInput} 
                value={formData.title} 
                onChange={handleInputChange} 
                required 
                placeholder="e.g. 5kW Rooftop Solar System" 
              />
            </div>

            <div className={styles.formGroup}>
              <label>Category</label>
              <select 
                name="category" 
                className={styles.formInput} 
                value={formData.category} 
                onChange={handleInputChange}
              >
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Institutional">Institutional</option>
                <option value="Events">Events</option>
                <option value="Team">Team</option>
              </select>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className={styles.formGroup}>
              <label>Location</label>
              <input 
                type="text" 
                name="location" 
                className={styles.formInput} 
                value={formData.location} 
                onChange={handleInputChange} 
                required 
                placeholder="e.g. Kankarbagh, Patna" 
              />
            </div>

            <div className={styles.formGroup}>
              <label>Upload Image</label>
              <div style={{position: 'relative'}}>
                <input 
                  type="file" 
                  id="fileInput" 
                  className={styles.formInput} 
                  onChange={handleFileChange} 
                  accept="image/*" 
                  required 
                />
              </div>
              <small style={{color: '#64748b', marginTop: '5px', display: 'block'}}>
                Supported formats: JPG, PNG, WEBP (Max 5MB)
              </small>
            </div>
          </div>

          {/* Full Width Button */}
          <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              {loading ? 'Uploading...' : <><FaCloudUploadAlt size={20} /> Upload to Gallery</>}
            </button>
          </div>
        </form>
      </div>

      {/* --- Images List Section --- */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
        <h3 style={{ color: 'white', margin: 0 }}>Existing Gallery ({images.length})</h3>
        <span style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Latest projects shown first</span>
      </div>
      
      <div className="gallery-grid-admin" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '25px' }}>
        {images.map((img) => (
          <div key={img.id} style={{ 
            background: 'rgba(255, 255, 255, 0.03)', 
            borderRadius: '12px', 
            overflow: 'hidden', 
            border: '1px solid rgba(255, 255, 255, 0.08)',
            transition: 'transform 0.2s ease',
            position: 'relative'
          }}>
            <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
              <img 
                src={img.imageUrl.startsWith('http') ? img.imageUrl : `${config.IMG_BASE_URL}${img.imageUrl}`} 
                alt={img.title} 
                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }} 
              />
              <div style={{
                position: 'absolute', top: '10px', right: '10px', 
                background: 'rgba(0,0,0,0.6)', color: 'white', 
                padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem',
                backdropFilter: 'blur(4px)'
              }}>
                {img.category}
              </div>
            </div>
            
            <div style={{ padding: '16px' }}>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '8px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {img.title}
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: '#94a3b8', marginBottom: '20px' }}>
                <FaMapMarkerAlt style={{marginRight: '6px', color: '#4ade80'}} /> {img.location}
              </div>
              
              {/* Updated Delete Button using Module Class */}
              <button 
                onClick={() => handleDelete(img.id)}
                className={styles.deleteBtn}
              >
                <FaTrash /> Delete Image
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminGallery;